/**
 * YOLOv8 ball detector via ONNX Runtime Web.
 * Loads custom-trained yolo-juggle.onnx from public/models/.
 */
import * as ort from 'onnxruntime-web';

export interface YoloDetection {
  x: number;
  y: number;
  w: number;
  h: number;
  confidence: number;
}

export interface YoloMetadata {
  inputSize: number;
  classes: string[];
  confidenceThreshold: number;
  nmsIouThreshold: number;
}

const DEFAULT_METADATA: YoloMetadata = {
  inputSize: 640,
  classes: ['ball'],
  confidenceThreshold: 0.45,
  nmsIouThreshold: 0.5,
};

export class YoloBallDetector {
  private session: ort.InferenceSession | null = null;
  private metadata: YoloMetadata = DEFAULT_METADATA;
  private initialized = false;
  private initPromise: Promise<void> | null = null;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D | null = null;

  constructor() {
    this.canvas = document.createElement('canvas');
    this.ctx = this.canvas.getContext('2d');
  }

  async initialize(): Promise<void> {
    if (this.initialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async () => {
      try {
        const modelPath = `${process.env.PUBLIC_URL ?? ''}/models/yolo-juggle.onnx`;
        const metaPath = `${process.env.PUBLIC_URL ?? ''}/models/metadata.json`;

        const [session, meta] = await Promise.all([
          ort.InferenceSession.create(modelPath, {
            executionProviders: ['webgl', 'wasm'],
            graphOptimizationLevel: 'all',
          }),
          fetch(metaPath)
            .then((r) => r.json())
            .catch(() => null),
        ]);

        this.session = session;
        if (meta) {
          this.metadata = { ...DEFAULT_METADATA, ...meta };
        }
        this.initialized = true;
      } catch (err) {
        throw new Error(
          `Failed to load YOLO model. Train a ball detector and add yolo-juggle.onnx to public/models/. See JuggleVisionSpec.md. ${err instanceof Error ? err.message : String(err)}`
        );
      }
    })();

    return this.initPromise;
  }

  isReady(): boolean {
    return this.initialized && this.session != null;
  }

  async detect(
    video: HTMLVideoElement,
    maxDetections: number = 5
  ): Promise<YoloDetection[]> {
    if (!this.session || !this.ctx) return [];

    const size = this.metadata.inputSize;
    const { data, padX, padY, scale } = this.letterbox(video, size);

    const inputTensor = new ort.Tensor('float32', data, [1, 3, size, size]);
    const outputs = await this.session.run({ images: inputTensor });
    const raw = outputs[this.session.outputNames[0]] as ort.Tensor;

    const detections = this.postProcess(raw, size, this.metadata.confidenceThreshold, this.metadata.nmsIouThreshold);
    const mapped = detections.slice(0, maxDetections).map((d) => ({
      ...d,
      x: (d.x - padX) / scale,
      y: (d.y - padY) / scale,
      w: d.w / scale,
      h: d.h / scale,
    }));
    return mapped;
  }

  private letterbox(
    video: HTMLVideoElement,
    size: number
  ): { data: Float32Array; padX: number; padY: number; scale: number } {
    const { videoWidth: w, videoHeight: h } = video;
    const scale = Math.min(size / w, size / h);
    const nw = Math.round(w * scale);
    const nh = Math.round(h * scale);
    const padX = (size - nw) / 2;
    const padY = (size - nh) / 2;

    this.canvas.width = size;
    this.canvas.height = size;
    this.ctx!.fillStyle = '#114';
    this.ctx!.fillRect(0, 0, size, size);
    this.ctx!.drawImage(video, 0, 0, w, h, padX, padY, nw, nh);

    const imageData = this.ctx!.getImageData(0, 0, size, size);
    const data = new Float32Array(1 * 3 * size * size);
    const mean = [0, 0, 0];
    const std = [255, 255, 255];

    for (let i = 0; i < size * size; i++) {
      const r = imageData.data[i * 4];
      const g = imageData.data[i * 4 + 1];
      const b = imageData.data[i * 4 + 2];
      data[0 * size * size + i] = (r - mean[0]) / std[0];
      data[1 * size * size + i] = (g - mean[1]) / std[1];
      data[2 * size * size + i] = (b - mean[2]) / std[2];
    }

    return { data, padX, padY, scale };
  }

  private postProcess(
    tensor: ort.Tensor,
    size: number,
    confThresh: number,
    iouThresh: number
  ): YoloDetection[] {
    const shape = tensor.dims;
    const data = tensor.data as Float32Array;
    const [, channels, numPreds] = shape;

    const numClasses = channels - 4;
    const detections: Array<{ x: number; y: number; w: number; h: number; confidence: number }> = [];

    for (let i = 0; i < numPreds; i++) {
      const cx = data[0 * numPreds + i];
      const cy = data[1 * numPreds + i];
      const bw = data[2 * numPreds + i];
      const bh = data[3 * numPreds + i];
      let maxConf = 0;
      for (let c = 0; c < numClasses; c++) {
        const conf = data[(4 + c) * numPreds + i];
        if (conf > maxConf) maxConf = conf;
      }
      if (maxConf < confThresh) continue;

      detections.push({
        x: cx,
        y: cy,
        w: bw,
        h: bh,
        confidence: maxConf,
      });
    }

    return this.nms(detections, iouThresh);
  }

  private nms(
    detections: Array<{ x: number; y: number; w: number; h: number; confidence: number }>,
    iouThresh: number
  ): YoloDetection[] {
    const sorted = [...detections].sort((a, b) => b.confidence - a.confidence);
    const picked: YoloDetection[] = [];

    for (const det of sorted) {
      const x1 = det.x - det.w / 2;
      const y1 = det.y - det.h / 2;
      const x2 = det.x + det.w / 2;
      const y2 = det.y + det.h / 2;

      let skip = false;
      for (const p of picked) {
        const px1 = p.x - p.w / 2;
        const py1 = p.y - p.h / 2;
        const px2 = p.x + p.w / 2;
        const py2 = p.y + p.h / 2;
        const interW = Math.max(0, Math.min(x2, px2) - Math.max(x1, px1));
        const interH = Math.max(0, Math.min(y2, py2) - Math.max(y1, py1));
        const inter = interW * interH;
        const area = det.w * det.h;
        const pArea = p.w * p.h;
        const iou = inter / (area + pArea - inter);
        if (iou > iouThresh) {
          skip = true;
          break;
        }
      }
      if (!skip) picked.push(det);
    }
    return picked;
  }
}
